import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, BarChart3, TrendingUp, Clock } from "lucide-react";
import { MarketSummaryService, type AnalysisRequest } from '@/services/MarketSummaryService';
import { SymbolService, type SymbolData } from '@/services/SymbolService';
import { toast } from "sonner";

interface MarketAnalysisFormProps {
  onAnalysisComplete: (summaryId: string) => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
}

export const MarketAnalysisForm = ({ onAnalysisComplete, isAnalyzing, setIsAnalyzing }: MarketAnalysisFormProps) => {
  const [symbol, setSymbol] = useState('EURUSD');
  const [limit, setLimit] = useState(100);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [endTime, setEndTime] = useState('23:59');
  const [availableSymbols, setAvailableSymbols] = useState<SymbolData[]>([]);

  useEffect(() => {
    loadAvailableSymbols();
  }, []);

  const loadAvailableSymbols = async () => {
    try {
      const result = await SymbolService.getAvailableSymbolsFromIdeas();
      if (result.success && result.data) {
        setAvailableSymbols(result.data);
      } else {
        console.error('Error loading symbols:', result.error);
      }
    } catch (error) {
      console.error('Error loading symbols:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!symbol.trim()) {
      toast.error('Please enter a symbol');
      return;
    }

    if (limit < 1 || limit > 1000) {
      toast.error('Limit must be between 1 and 1000');
      return;
    }

    // Combine date and time for proper datetime
    const startDateTime = new Date(`${startDate}T${startTime}:00`);
    const endDateTime = new Date(`${endDate}T${endTime}:00`);

    if (startDateTime >= endDateTime) {
      toast.error('Start date and time must be before end date and time');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const request: AnalysisRequest = {
        symbol: symbol.trim().toUpperCase(),
        limit,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString()
      };

      const result = await MarketSummaryService.analyzeMarketIdeas(request);
      
      if (result.success && result.summaryId) {
        toast.success('Market analysis completed successfully');
        onAnalysisComplete(result.summaryId);
      } else {
        toast.error(result.error || 'Failed to analyze market data');
      }
    } catch (error) {
      console.error('Error during analysis:', error);
      toast.error('Failed to analyze market data');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-green-500" />
          Market Data Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Trading Symbol</Label>
            {availableSymbols.length > 0 ? (
              <Select value={symbol} onValueChange={setSymbol}>
                <SelectTrigger>
                  <SelectValue placeholder="Select symbol" />
                </SelectTrigger>
                <SelectContent>
                  {availableSymbols.map((symbolData) => (
                    <SelectItem key={symbolData.value} value={symbolData.value}>
                      {symbolData.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="e.g., EURUSD"
                disabled={isAnalyzing}
              />
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="limit">Number of Ideas</Label>
            <Input
              id="limit"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              min={1}
              max={1000}
              disabled={isAnalyzing}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-500" />
              <Label className="text-sm font-medium">Start Date & Time</Label>
            </div>
            <div className="space-y-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isAnalyzing}
              />
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={isAnalyzing}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-500" />
              <Label className="text-sm font-medium">End Date & Time</Label>
            </div>
            <div className="space-y-2">
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isAnalyzing}
              />
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={isAnalyzing}
                />
              </div>
            </div>
          </div>
        </div>

        <Button
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          className="w-full bg-green-500 hover:bg-green-600"
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          {isAnalyzing ? 'Analyzing...' : 'Analyze Market Data'}
        </Button>
      </CardContent>
    </Card>
  );
};
